/**
 * FreeSampleProgress — Smart Progress Banner (unified layout across all states).
 *
 * One reusable component; only headline / supporting-text / CTA change based on
 * the user's remaining free-sample downloads. Paid users retain the existing
 * compact "downloads remaining" summary (unchanged behavior).
 *
 * Preserves: prop signature (downloadsUsed, freeLimit, onUpgradeClick, planId),
 * data-testid hooks, analytics/CTA wiring, sticky positioning of the parent.
 */
import { ArrowDown, ArrowRight, CheckCircle2 } from "lucide-react";

const scrollToGrid = () => {
  const grid = document.querySelector('[data-testid="image-grid"]');
  if (grid) {
    grid.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.scrollBy({ top: 400, behavior: "smooth" });
  }
};

export function FreeSampleProgress({ downloadsUsed, freeLimit, onUpgradeClick, planId }) {
  const remaining = Math.max(0, freeLimit - downloadsUsed);
  const progress = freeLimit > 0 ? (downloadsUsed / freeLimit) * 100 : 0;
  const isFree = !planId || planId === "free";

  /* Paid users keep the compact summary (unchanged behavior). */
  if (!isFree) {
    return (
      <div
        className="rounded-lg border border-emerald-100 bg-emerald-50/50 shadow-sm px-4 py-3 flex items-center justify-between gap-3"
        data-testid="free-sample-progress"
      >
        <div>
          <p className="text-[13.5px] font-semibold text-emerald-800" data-testid="free-trial-banner-message">
            Downloads Remaining
          </p>
          <p className="text-[11.5px] text-slate-500" data-testid="free-sample-count">
            {downloadsUsed} used • {remaining} remaining
          </p>
        </div>
        <div className="h-1.5 bg-slate-200/60 rounded-full overflow-hidden w-[140px]">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${Math.min(100, progress)}%` }}
            data-testid="free-sample-progress-bar"
          />
        </div>
      </div>
    );
  }

  /* Free-user states — choose copy + CTA based on `remaining`. */
  const state =
    remaining === 0
      ? {
          headline: (
            <>
              Free Sample Downloads Complete
              <CheckCircle2 className="inline-block ml-1.5 h-4 w-4 -mt-0.5 text-emerald-600" strokeWidth={2.5} />
            </>
          ),
          sub: "Unlock Studio Pass to continue downloading.",
          ctaLabel: "Unlock Studio Pass",
          ctaIconRight: <ArrowRight className="h-3.5 w-3.5" />,
          onClick: onUpgradeClick,
          testId: "free-sample-upgrade-btn",
          isUpgrade: true,
        }
      : remaining === 1
      ? {
          headline: "1 Free Sample Download Remaining",
          sub: "One last sample before unlocking the complete library.",
          ctaLabel: "Continue Exploring",
          ctaIconRight: <ArrowDown className="h-3.5 w-3.5" />,
          onClick: scrollToGrid,
          testId: "free-sample-explore-btn",
          isUpgrade: false,
        }
      : {
          headline: `${remaining} Free Sample Downloads Remaining`,
          sub: "Download any 2 dishes. Upgrade only if you're satisfied.",
          ctaLabel: "Explore Photos",
          ctaIconRight: <ArrowDown className="h-3.5 w-3.5" />,
          onClick: scrollToGrid,
          testId: "free-sample-explore-btn",
          isUpgrade: false,
        };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-3 flex items-center justify-between gap-3"
      data-testid="free-sample-progress"
      data-state={remaining === 0 ? "complete" : remaining === 1 ? "last" : "start"}
    >
      <div className="min-w-0 flex-1">
        <p
          className="text-[14px] sm:text-[15px] font-bold text-slate-900 leading-snug tracking-tight"
          data-testid="free-trial-banner-message"
        >
          {state.headline}
        </p>
        <p className="text-[12px] text-slate-500 leading-snug mt-0.5" data-testid="free-sample-count">
          {state.sub}
        </p>

        {/* Compact progress track (always visible for continuity) */}
        <div className="mt-2 h-1 bg-slate-200/70 rounded-full overflow-hidden w-full max-w-[220px]">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              remaining === 0 ? "bg-[#ff5a00]" : remaining === 1 ? "bg-amber-500" : "bg-emerald-500"
            }`}
            style={{ width: `${Math.min(100, progress)}%` }}
            data-testid="free-sample-progress-bar"
          />
        </div>
      </div>

      <button
        onClick={state.onClick}
        data-testid={state.testId}
        className={`shrink-0 h-9 px-3.5 rounded-full inline-flex items-center gap-1.5 text-[12.5px] font-bold transition-all duration-200 ${
          state.isUpgrade
            ? "text-white shadow-[0_4px_12px_rgba(255,90,0,0.28)]"
            : "text-[#ff5a00] border border-[#ffd9c2] bg-[#fff6ee] hover:bg-[#ffe5d5]"
        }`}
        style={
          state.isUpgrade
            ? { background: "linear-gradient(90deg, #ff8a3d 0%, #ff5a00 100%)" }
            : undefined
        }
      >
        {state.ctaLabel}
        {state.ctaIconRight}
      </button>
    </div>
  );
}
